import React from 'react';
import { Grid2, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { brandTokens } from '../single-page/theme';

export const CATEGORIES = [
  {
    id: 'historico',
    label: 'Histórico',
    Icon: HistoryOutlinedIcon,
    description:
      'Consulta o histórico completo de proprietários, transferências e ocorrências do veículo.',
  },
  {
    id: 'multas',
    label: 'Multas',
    Icon: DescriptionOutlinedIcon,
    description:
      'Verifica todas as multas de trânsito registradas, ativas e pendentes de pagamento.',
  },
  {
    id: 'ipva',
    label: 'IPVA',
    Icon: AccountBalanceOutlinedIcon,
    description:
      'Exibe a situação do IPVA, exercícios quitados e débitos em aberto.',
  },
  {
    id: 'documentos',
    label: 'Documentos',
    Icon: ArticleOutlinedIcon,
    description:
      'Analisa a regularidade documental do veículo junto aos órgãos oficiais.',
  },
  {
    id: 'licenciamento',
    label: 'Licenciamento',
    Icon: VerifiedOutlinedIcon,
    description:
      'Confirma o licenciamento anual e se o veículo está regular para circular.',
  },
  {
    id: 'sinistro-leilao',
    label: 'Sinistro/Leilão',
    Icon: GavelOutlinedIcon,
    description:
      'Identifica registros de sinistro, perda total e passagens por leilão.',
  },
  {
    id: 'restricoes',
    label: 'Restrições',
    Icon: ShieldOutlinedIcon,
    description:
      'Detecta restrições administrativas, judiciais e de alienação fiduciária.',
  },
  {
    id: 'debitos',
    label: 'Débitos',
    Icon: WarningAmberOutlinedIcon,
    description:
      'Reúne todos os débitos do veículo em tempo real para quitação rápida.',
  },
];

const RootGrid = styled(Grid2)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  margin: 0,
  padding: 0,
  backgroundColor: brandTokens.navy,
  overflow: 'hidden',
}));

const ContentBlock = styled('div')(({ theme }) => ({
  color: '#FFFFFF',
  width: '100%',
  maxWidth: 1280,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(10, 3),
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 2),
  },
}));

const TitleMain = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  fontFamily: 'GT Haptik, Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '40px',
  lineHeight: 1.2,
  maxWidth: 800,
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
  },
}));

const HighlightWord = styled('span')({
  background: brandTokens.signatureGradient,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

const SubTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(6),
  fontFamily: 'Roboto, sans-serif',
  fontSize: '16px',
  lineHeight: 1.6,
  maxWidth: 640,
  color: '#8FA3B8',
}));

const BentoCard = styled(Box)(({ theme }) => ({
  height: '100%',
  backgroundColor: brandTokens.tonal,
  padding: theme.spacing(3),
  borderRadius: 16,
  border: '1px solid rgba(107, 211, 253, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'border-color 0.2s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: 3,
    height: '100%',
    backgroundColor: brandTokens.cyan,
    opacity: 0,
    transition: 'opacity 0.2s ease',
  },
  '&:hover': {
    borderColor: 'rgba(107, 211, 253, 0.55)',
    '&::before': {
      opacity: 1,
    },
  },
}));

const IconBox = styled(Box)({
  width: 48,
  height: 48,
  borderRadius: 12,
  backgroundColor: 'rgba(107, 211, 253, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 20,
  '& svg': {
    color: brandTokens.cyan,
    fontSize: 24,
  },
});

const VehicleAnalysis = () => {
  return (
    <RootGrid container>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800,
          height: 800,
          bgcolor: 'rgba(107, 211, 253, 0.05)',
          filter: 'blur(120px)',
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />
      <ContentBlock>
        <TitleMain variant="h2">
          Análise completa em pontos <HighlightWord>verificados</HighlightWord>
        </TitleMain>
        <SubTitle>
          Nossa tecnologia cruza dados de fontes oficiais para garantir que você
          nunca compre um problema.
        </SubTitle>
        <Grid2 container spacing={2.5}>
          {CATEGORIES.map(({ id, label, Icon, description }) => (
            <Grid2 size={{ xs: 12, sm: 6, lg: 3 }} key={id}>
              <BentoCard>
                <IconBox>
                  <Icon aria-hidden />
                </IconBox>
                <Typography
                  sx={{
                    fontFamily: 'GT Haptik, Roboto, sans-serif',
                    fontWeight: 600,
                    fontSize: 18,
                    color: brandTokens.onSurface,
                    mb: 1,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: brandTokens.onSurfaceVariant,
                  }}
                >
                  {description}
                </Typography>
              </BentoCard>
            </Grid2>
          ))}
        </Grid2>
      </ContentBlock>
    </RootGrid>
  );
};

export default VehicleAnalysis;
